import React, { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/Header";
import { Sidebar } from "@/components/Sidebar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useFlyerAccess } from "@/hooks/use-admin-auth";
import { AdminAccessState } from "@/components/AdminAccessState";
import { useTimezone } from "@/contexts/TimezoneContext";
import { toast } from "@/hooks/use-toast";
import {
  Search, RefreshCw, Send, Mail, Users, ShieldCheck, ChevronLeft, ChevronRight,
  XCircle, History, CheckCircle2, AlertTriangle, Loader2, Rocket, Ban, Upload, Eye,
} from "lucide-react";

/* ===================== CONSTANTS ===================== */

// Mirrors campaign/email_validation.py MAX_VALIDATION_BATCH — the cap for
// the synchronous single-check path (both providers). Selections larger than
// this route through NeverBounce's bulk Jobs API instead (Mailgun has no
// bulk-job equivalent here, so it stays capped at this number).
const MAX_VALIDATION_BATCH = 500;
// Mirrors campaign/email_validation.py MAX_BULK_VALIDATION_BATCH.
const MAX_BULK_VALIDATION_BATCH = 10000;
// Mirrors campaign/email_validation.py RECENT_VALIDATION_FRESHNESS_DAYS —
// display-only (the backend enforces the actual skip), just so the "cached"
// hint below can say how recent "recent" means.
const RECENT_VALIDATION_FRESHNESS_DAYS = 15;
const BULK_JOB_POLL_INTERVAL_MS = 5000;
const DEFAULT_LEADS_PAGE_SIZE = 50;
// Backend (flyer_control_service.search_leads) caps page_size at 1000.
const LEADS_PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500, 1000];

/* ===================== TYPES ===================== */

interface Lead {
  id: number;
  email: string;
  name: string;
  company_name: string;
  mail_status: string;
  last_flyer_sent_at: string | null;
  flyer_sent_count: number;
  // Mailgun webhook engagement tracking (flyer sends only — see
  // campaign/webhooks.py). Null means "never confirmed opened/clicked",
  // whether because the flyer was never sent or was sent but not engaged
  // with — the filter below treats both the same way.
  last_opened_at: string | null;
  last_clicked_at: string | null;
  last_validated_at: string | null;
  last_validation_status: string | null;
  validation_count: number;
}

/**
 * One row in Activity & History — either an actual flyer send ("flyer") or
 * a completed/running validation job that hasn't had a flyer sent from it
 * yet ("validation"). The two never coexist for the same batch: the moment
 * a flyer is launched from a validation job, the backend stops returning
 * that job's row at all — its "validated": true flag on the resulting
 * flyer row tells the same story from then on, so there's exactly one row
 * per batch, never two.
 */
interface FlyerJob {
  type: "flyer";
  job_id: number;
  subject: string;
  status: string;
  total: number;
  sent: number;
  failed: number;
  pending: number;
  progress_pct: number;
  current_batch_step: number;
  next_batch_size: number;
  next_delay_seconds: number;
  started_at: string | null;
  created_by: string | null;
  validated: boolean;
}

interface ValidationJobRow {
  type: "validation";
  job_id: number;
  status: "running" | "complete" | "failed";
  total: number;
  valid: number;
  invalid: number;
  not_guaranteed: number;
  percent_complete: number;
  failure_reason: string | null;
  created_by: string | null;
  created_at: string;
  completed_at: string | null;
}

type ActivityRow = FlyerJob | ValidationJobRow;

interface FlyerMeta {
  import_sources: string[];
  active_agent_count: number;
  advertisement_lead_count: number;
  default_subject: string;
  default_template: string;
  max_selected_emails: number;
  validation_providers: ValidationProvider[];
  default_validation_provider: ValidationProvider;
}

interface ValidationQuota {
  provider: ValidationProvider;
  limit: number;
  used: number;
  remaining: number;
  month: string;
}

type ValidationProvider = "neverbounce" | "mailgun";

const VALIDATION_PROVIDER_LABELS: Record<ValidationProvider, string> = {
  neverbounce: "NeverBounce",
  mailgun: "Mailgun",
};

type LaunchMode = "full" | "limit" | "selected";

/** A NeverBounce bulk validation job — polled until it reaches a terminal state. */
interface BulkJob {
  job_id: number;
  status: "running" | "complete" | "failed";
  total: number;
  percent_complete: number;
  valid: number;
  invalid: number;
  not_guaranteed: number;
  failure_reason: string | null;
  created_at: string;
  completed_at: string | null;
}

/* ===================== HELPERS ===================== */

const Skeleton = ({ className }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const StatCard = ({
  label, value, icon, valueClass, loading,
}: {
  label: string; value: string | number; icon: React.ReactNode;
  valueClass: string; loading: boolean;
}) => (
  <div className="bg-white rounded-2xl border border-gray-300 px-5 py-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="p-2.5 rounded-xl bg-gray-50 flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-400 mb-1 font-medium">{label}</p>
      {loading
        ? <Skeleton className="h-7 w-16" />
        : <p className={`text-xl font-bold leading-none truncate ${valueClass}`}>{value}</p>
      }
    </div>
  </div>
);

const getPaginationItems = (current: number, total: number): Array<number | "..."> => {
  if (total <= 7) return Array.from({ length: Math.max(total, 1) }, (_, i) => i + 1);
  const items: Array<number | "..."> = [];
  const ws = Math.max(2, current - 1);
  const we = Math.min(total - 1, current + 1);
  items.push(1);
  if (ws > 2) items.push("...");
  for (let p = ws; p <= we; p++) items.push(p);
  if (we < total - 1) items.push("...");
  items.push(total);
  return items;
};

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700",
  running: "bg-blue-50 text-blue-700",
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
};

const StatusBadge = ({ status }: { status: string }) => (
  <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[status] || "bg-gray-100 text-gray-500"}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${(STATUS_STYLES[status] || "").split(" ")[1] || "bg-gray-400"} bg-current`} />
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

const VALIDATION_STYLES: Record<string, string> = {
  valid: "text-emerald-700 bg-emerald-50",
  invalid: "text-red-700 bg-red-50",
  // NeverBounce "catchall" — sendable (passes the launch gate same as
  // valid) but kept visually distinct since the mailbox itself was never
  // individually confirmed.
  not_guaranteed: "text-amber-700 bg-amber-50",
};

const VALIDATION_STATUS_LABELS: Record<string, string> = {
  valid: "Valid",
  invalid: "Invalid",
  not_guaranteed: "Not Guaranteed",
};

// Mirrors NEVERBOUNCE_NOT_GUARANTEED_RESULTS in campaign/email_validation.py
// exactly — keep these two in sync if that set ever changes again. "unknown"
// joined "catchall" here after real testing showed brand-new mailboxes
// routinely come back "unknown" yet are genuinely deliverable.
const NEVERBOUNCE_NOT_GUARANTEED_RESULTS = new Set(["catchall", "unknown"]);

/**
 * The 3-way lead status a single-check validation result should drive —
 * mirrors the backend's _lead_status_for() in email_validation.py.
 * NeverBounce "catchall"/"unknown" are their own "not_guaranteed" status
 * (sendable, same as valid for the launch gate) rather than being folded
 * into "invalid" just because r.is_valid is false — that field is a strict
 * per-provider verdict, not the sendability status.
 */
const leadStatusForResult = (r: { is_valid: boolean; result?: string }, provider: ValidationProvider): "valid" | "invalid" | "not_guaranteed" => {
  if (provider === "neverbounce" && r.result && NEVERBOUNCE_NOT_GUARANTEED_RESULTS.has(r.result)) return "not_guaranteed";
  return r.is_valid ? "valid" : "invalid";
};

const isSendableResult = (r: { is_valid: boolean; result?: string }, provider: ValidationProvider) =>
  leadStatusForResult(r, provider) !== "invalid";

/* ===================== COMPONENT ===================== */

const FlyerControl: React.FC = () => {
  const toggleMobileRef = useRef<(() => void) | null>(null);
  const { status: authStatus, retrying, retry, permissions } = useFlyerAccess();
  const { formatDateTime } = useTimezone();

  /* ── Meta / quota ── */
  const [meta, setMeta] = useState<FlyerMeta | null>(null);
  const [quota, setQuota] = useState<ValidationQuota | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);
  // Which provider the "Validate" action + quota stat cards use. Defaults to
  // NeverBounce (see settings.EMAIL_VALIDATION_DEFAULT_PROVIDER) until /meta/
  // reports the server's actual configured default.
  const [validationProvider, setValidationProvider] = useState<ValidationProvider>("neverbounce");
  const [quotaLoading, setQuotaLoading] = useState(false);

  const fetchQuota = async (provider: ValidationProvider) => {
    setQuotaLoading(true);
    try {
      const res = await axiosInstance.get(`${endpoints.flyer_validation_quota}?provider=${provider}`);
      setQuota(res.data);
    } catch {
      setQuota(null);
    } finally {
      setQuotaLoading(false);
    }
  };

  const fetchMeta = async () => {
    setMetaLoading(true);
    try {
      const metaRes = await axiosInstance.get(endpoints.flyer_meta);
      setMeta(metaRes.data);
      if (metaRes.data?.default_validation_provider) {
        setValidationProvider(metaRes.data.default_validation_provider);
      }
      await fetchQuota(metaRes.data?.default_validation_provider || validationProvider);
    } catch {
      // stat cards just stay in their loading/blank state
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    if (authStatus === "admin") fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus]);

  // Re-pull the quota banner whenever the picker's provider dropdown changes
  // (not on the initial mount — fetchMeta above already fetches it once it
  // knows the server's default provider).
  const isFirstProviderRender = useRef(true);
  useEffect(() => {
    if (isFirstProviderRender.current) { isFirstProviderRender.current = false; return; }
    if (authStatus === "admin") fetchQuota(validationProvider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationProvider]);

  /* ── NeverBounce bulk validation job (selections > MAX_VALIDATION_BATCH) ──
   * Runs entirely in the background: the persistent status strip below the
   * header keeps tracking it via polling even if the Launch dialog that
   * started it gets closed, and a page reload picks the same job back up
   * via flyer_validate_bulk_active — nothing is lost by navigating away. */
  const [bulkJob, setBulkJob] = useState<BulkJob | null>(null);
  const [bulkJobDismissed, setBulkJobDismissed] = useState(false);
  const [startingBulkJob, setStartingBulkJob] = useState(false);

  // Resume tracking an already-running job after a page reload.
  useEffect(() => {
    if (authStatus !== "admin") return;
    axiosInstance.get(endpoints.flyer_validate_bulk_active)
      .then((res) => { if (res.status !== 204 && res.data) setBulkJob(res.data); })
      .catch(() => {});
  }, [authStatus]);

  /* ── "Unsent validated batch" recovery banner ──
   * A bulk job's results outlive the page (they're in the DB), but the
   * in-memory selection that would carry them to Launch does not — a
   * reload right after a job finishes otherwise strands the user with no
   * way back to what was just validated. This surfaces the latest
   * completed job's still-unsent emails independent of bulkJob/session
   * state, so it works the same whether the job just finished in this tab
   * or finished before the page was even loaded. */
  interface PendingSendJob extends BulkJob { pending_send_count: number }
  const [pendingSendJob, setPendingSendJob] = useState<PendingSendJob | null>(null);
  const [pendingSendDismissed, setPendingSendDismissed] = useState(false);
  const [loadingPendingSend, setLoadingPendingSend] = useState(false);

  const fetchPendingSendJob = () => {
    axiosInstance.get(endpoints.flyer_validate_bulk_pending)
      .then((res) => {
        if (res.status !== 204 && res.data) {
          setPendingSendJob(res.data);
          setPendingSendDismissed(false);
        } else {
          setPendingSendJob(null);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (authStatus === "admin") fetchPendingSendJob();
  }, [authStatus]);

  // Tracks which validation job (if any) the current Launch-modal selection
  // came from, so handleLaunch can pass validation_job_id along and the
  // backend can link the resulting FlyerSendJob back to it — that link is
  // what lets this validation job's row disappear from Activity & History
  // once the send is queued, instead of lingering alongside its flyer row.
  const [launchValidationJobId, setLaunchValidationJobId] = useState<number | null>(null);

  const selectAndSendForJob = async (jobId: number, emailsUrl: string, onEmpty: () => void) => {
    if (loadingPendingSend) return;
    setLoadingPendingSend(true);
    try {
      const res = await axiosInstance.get(emailsUrl);
      const emails: string[] = res.data.emails || [];
      if (emails.length === 0) {
        toast({
          title: "Nothing left to send",
          description: "These emails may have already been sent since this was last checked.",
        });
        onEmpty();
        return;
      }
      setSelectedEmails(new Set(emails));
      setSelectedLeadDetails(Object.fromEntries(emails.map((email) => [email, {
        id: 0, email, name: "", company_name: "", mail_status: "active",
        last_flyer_sent_at: null, flyer_sent_count: 0, last_validated_at: null,
        // Every email this endpoint returns already passed the sendable
        // check server-side (valid or not_guaranteed) — "valid" here is
        // just a placeholder that satisfies the launch gate, not a claim
        // about which exact status it was.
        last_validation_status: "valid", validation_count: 0,
      } as Lead])));
      setLaunchValidationJobId(jobId);
      openLaunchModal("selected");
    } catch {
      toast({
        title: "Couldn't load pending emails",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPendingSend(false);
    }
  };

  const handleSelectAndSendPending = () => {
    if (!pendingSendJob) return;
    selectAndSendForJob(pendingSendJob.job_id, endpoints.flyer_validate_bulk_pending_emails, () => setPendingSendJob(null));
  };

  const handleSendFlyerForJob = (job: ValidationJobRow) => {
    selectAndSendForJob(job.job_id, endpoints.flyer_validate_bulk_job_pending_emails(job.job_id), () => fetchJobs());
  };

  useEffect(() => {
    if (!bulkJob || bulkJob.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const res = await axiosInstance.get(endpoints.flyer_validate_bulk_status(bulkJob.job_id));
        setBulkJob(res.data);
        if (res.data.status === "complete") {
          toast({
            title: "Bulk validation complete",
            description: `${res.data.valid.toLocaleString()} valid, ${res.data.not_guaranteed.toLocaleString()} not guaranteed, ${res.data.invalid.toLocaleString()} invalid out of ${res.data.total.toLocaleString()} emails.`,
          });
          setLeadsRefreshKey((k) => k + 1);
          fetchQuota("neverbounce");
          fetchPendingSendJob();
          setBulkJobDismissed(false);
        } else if (res.data.status === "failed") {
          toast({
            title: "Bulk validation failed",
            description: res.data.failure_reason || "NeverBounce reported this job as failed.",
            variant: "destructive",
          });
          setBulkJobDismissed(false);
        }
      } catch {
        // transient network hiccup — just try again on the next tick
      }
    }, BULK_JOB_POLL_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkJob?.job_id, bulkJob?.status]);

  const handleStartBulkValidation = async () => {
    if (selectedEmails.size === 0 || startingBulkJob) return;
    if (selectedEmails.size > MAX_BULK_VALIDATION_BATCH) {
      toast({
        title: "Too many selected",
        description: `Validate at most ${MAX_BULK_VALIDATION_BATCH.toLocaleString()} at a time — you have ${selectedEmails.size.toLocaleString()} selected.`,
        variant: "destructive",
      });
      return;
    }
    setStartingBulkJob(true);
    try {
      const res = await axiosInstance.post(endpoints.flyer_validate_bulk_start, { emails: Array.from(selectedEmails) });
      setBulkJob(res.data);
      setBulkJobDismissed(false);
      toast({
        title: "Bulk validation started",
        description: `Validating ${res.data.total.toLocaleString()} emails via NeverBounce in the background — you can keep working.`,
      });
    } catch (err: any) {
      toast({
        title: "Couldn't start bulk validation",
        description: err?.response?.data?.error || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setStartingBulkJob(false);
    }
  };

  /* ── Jobs (Activity & History) ── */
  const [jobs, setJobs] = useState<ActivityRow[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [showJobHistory, setShowJobHistory] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<FlyerJob | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchJobs = async () => {
    try {
      const res = await axiosInstance.get(endpoints.flyer_jobs);
      setJobs(res.data.jobs || []);
    } catch {
      // keep whatever was last successfully loaded
    } finally {
      setJobsLoading(false);
    }
  };

  useEffect(() => {
    if (authStatus === "admin") fetchJobs();
  }, [authStatus]);

  const hasActiveJob = jobs.some((j) => j.status === "pending" || j.status === "running");
  useEffect(() => {
    if (authStatus !== "admin" || !hasActiveJob) return;
    const t = setInterval(fetchJobs, 20000);
    return () => clearInterval(t);
  }, [authStatus, hasActiveJob]);

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    const target = cancelTarget;
    setCancelTarget(null);
    setCancelling(true);
    try {
      const res = await axiosInstance.post(endpoints.flyer_cancel(target.job_id));
      toast({ title: "Job cancelled", description: res.data?.message || `Job #${target.job_id} cancelled.` });
      fetchJobs();
    } catch (err: any) {
      toast({
        title: "Cancel failed",
        description: err?.response?.data?.error || "Could not cancel this job.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  /* ── Search & Select ── */
  const [source, setSource] = useState<"agents" | "advertisement_leads">("agents");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [flyerStatus, setFlyerStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [validationStatus, setValidationStatus] = useState("all");
  const [validatedDateFrom, setValidatedDateFrom] = useState("");
  const [validatedDateTo, setValidatedDateTo] = useState("");
  // One positive filter (all/opened/clicked), not a pair of yes/no toggles —
  // there's deliberately no "not opened"/"not clicked" option.
  const [engagementStatus, setEngagementStatus] = useState("all");
  const [importSource, setImportSource] = useState("");
  const [excludeDomains, setExcludeDomains] = useState("");

  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsFiltering, setLeadsFiltering] = useState(false);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPageSize, setLeadsPageSize] = useState(DEFAULT_LEADS_PAGE_SIZE);
  const [leadsCount, setLeadsCount] = useState(0);
  const [leadsNumPages, setLeadsNumPages] = useState(1);
  // Bumped after a successful Validate call to force a real re-fetch of the
  // current page — the DB write already happened instantly inside that call,
  // this just makes the picker's Validation column reflect it right away
  // instead of waiting for some other action (e.g. Launch) to refresh it.
  const [leadsRefreshKey, setLeadsRefreshKey] = useState(0);

  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  // Tracks the last-known Lead record for every currently-selected email, kept
  // up to date across page changes and validation runs — the launch gate below
  // needs each selection's last_validation_status even for emails selected on
  // a page that's no longer loaded.
  const [selectedLeadDetails, setSelectedLeadDetails] = useState<Record<string, Lead>>({});

  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(searchInput); setLeadsPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (authStatus !== "admin") return;
    const isInitial = leads.length === 0 && leadsLoading;
    if (!isInitial) setLeadsFiltering(true);

    const controller = new AbortController();
    const params = new URLSearchParams({
      source, page: String(leadsPage), page_size: String(leadsPageSize),
    });
    if (searchTerm.trim()) params.set("q", searchTerm.trim());
    if (flyerStatus !== "all") params.set("flyer_status", flyerStatus);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (validationStatus !== "all") params.set("validation_status", validationStatus);
    if (validatedDateFrom) params.set("validated_date_from", validatedDateFrom);
    if (validatedDateTo) params.set("validated_date_to", validatedDateTo);
    if (engagementStatus !== "all") params.set("engagement_status", engagementStatus);
    if (importSource && source === "agents") params.set("import_source", importSource);
    if (excludeDomains.trim()) params.set("exclude_domains", excludeDomains.trim());

    axiosInstance.get(`${endpoints.flyer_leads}?${params.toString()}`, { signal: controller.signal })
      .then((res) => {
        setLeads(res.data.results || []);
        setLeadsCount(res.data.count || 0);
        setLeadsNumPages(res.data.num_pages || 1);
      })
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setLeads([]); setLeadsCount(0); setLeadsNumPages(1);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLeadsLoading(false);
        setLeadsFiltering(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authStatus, source, leadsPage, leadsPageSize, searchTerm, flyerStatus, dateFrom, dateTo,
    validationStatus, validatedDateFrom, validatedDateTo, engagementStatus,
    importSource, excludeDomains, leadsRefreshKey,
  ]);

  // A previous Validate result only describes the selection as it was at that
  // moment — any change to the selection invalidates it, so the modal falls
  // back to a fresh "Validate" button instead of showing a stale result.
  const invalidateValidationResult = () => {
    setValidationResult(null);
    setShowValidationResult(false);
    // The selection no longer purely represents one validation job's
    // emails once it's manually touched — don't link an unrelated launch
    // back to a stale job id.
    setLaunchValidationJobId(null);
  };

  const toggleEmail = (lead: Lead) => {
    const willSelect = !selectedEmails.has(lead.email);
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (willSelect) next.add(lead.email); else next.delete(lead.email);
      return next;
    });
    setSelectedLeadDetails((prev) => {
      const next = { ...prev };
      if (willSelect) next[lead.email] = lead; else delete next[lead.email];
      return next;
    });
    invalidateValidationResult();
  };

  const pageAllSelected = leads.length > 0 && leads.every((l) => selectedEmails.has(l.email));
  const togglePageAll = () => {
    const selecting = !pageAllSelected;
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      leads.forEach((l) => { if (selecting) next.add(l.email); else next.delete(l.email); });
      return next;
    });
    setSelectedLeadDetails((prev) => {
      const next = { ...prev };
      leads.forEach((l) => { if (selecting) next[l.email] = l; else delete next[l.email]; });
      return next;
    });
    invalidateValidationResult();
  };

  const clearSelection = () => {
    setSelectedEmails(new Set());
    setSelectedLeadDetails({});
    invalidateValidationResult();
  };

  // Selected emails that aren't confirmed sendable — either never validated,
  // or validated invalid. "not_guaranteed" (NeverBounce catchall) passes
  // this gate same as "valid" — real-world testing confirmed it's sendable,
  // it just stays a distinct status for tracing/filtering. Drives the "must
  // validate first" launch gate.
  const unvalidatedSelectedCount = Array.from(selectedEmails).filter((email) => {
    const status = selectedLeadDetails[email]?.last_validation_status;
    return status !== "valid" && status !== "not_guaranteed";
  }).length;

  const resetFilters = () => {
    setSearchInput(""); setFlyerStatus("all"); setDateFrom(""); setDateTo("");
    setValidationStatus("all"); setValidatedDateFrom(""); setValidatedDateTo("");
    setEngagementStatus("all");
    setImportSource(""); setExcludeDomains(""); setLeadsPage(1);
  };

  // Secondary filters (import source, exclude domains, date ranges) stay
  // tucked behind "More filters" by default — only the three most-used
  // dropdowns (flyer/validation/engagement status) show up front, so the
  // bar doesn't read as seven controls at once.
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const FLYER_STATUS_LABELS: Record<string, string> = { sent: "Flyer: Previously sent", not_sent: "Flyer: Never sent" };
  const VALIDATION_FILTER_LABELS: Record<string, string> = {
    valid: "Validation: Valid", not_guaranteed: "Validation: Not Guaranteed",
    invalid: "Validation: Invalid", not_validated: "Validation: Not validated",
  };
  const ENGAGEMENT_FILTER_LABELS: Record<string, string> = { opened: "Engagement: Opened", clicked: "Engagement: Clicked" };

  // One chip per active (non-default) filter, each clearing just itself —
  // this is what actually answers "what's filtering the list right now"
  // without having to read every dropdown's current value.
  const activeFilterChips: { key: string; label: string; clear: () => void }[] = [
    flyerStatus !== "all" && { key: "flyer", label: FLYER_STATUS_LABELS[flyerStatus] || flyerStatus, clear: () => { setFlyerStatus("all"); setDateFrom(""); setDateTo(""); } },
    validationStatus !== "all" && { key: "validation", label: VALIDATION_FILTER_LABELS[validationStatus] || validationStatus, clear: () => setValidationStatus("all") },
    engagementStatus !== "all" && { key: "engagement", label: ENGAGEMENT_FILTER_LABELS[engagementStatus] || engagementStatus, clear: () => setEngagementStatus("all") },
    importSource && { key: "import", label: `Import: ${importSource}`, clear: () => setImportSource("") },
    excludeDomains.trim() && { key: "exclude", label: `Exclude: ${excludeDomains.trim()}`, clear: () => setExcludeDomains("") },
    (dateFrom || dateTo) && { key: "sentdate", label: `Sent ${dateFrom || "…"} – ${dateTo || "…"}`, clear: () => { setDateFrom(""); setDateTo(""); } },
    (validatedDateFrom || validatedDateTo) && { key: "validateddate", label: `Validated ${validatedDateFrom || "…"} – ${validatedDateTo || "…"}`, clear: () => { setValidatedDateFrom(""); setValidatedDateTo(""); } },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  /* ── History modal (flyer sent / validation) ── */
  const [historyTarget, setHistoryTarget] = useState<{ email: string; type: "flyer" | "validation" } | null>(null);
  const [historyData, setHistoryData] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (email: string, type: "flyer" | "validation") => {
    setHistoryTarget({ email, type });
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const url = type === "flyer" ? endpoints.flyer_history : endpoints.flyer_validation_history;
      const res = await axiosInstance.get(`${url}?email=${encodeURIComponent(email)}`);
      setHistoryData(res.data);
    } catch {
      setHistoryData({ error: "Could not load history." });
    } finally {
      setHistoryLoading(false);
    }
  };

  /* ── Validate Selected ── */
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  // Controls whether the result breakdown is shown — the Validate button
  // itself turns into a "See Result" / "Hide Result" toggle once a result exists.
  const [showValidationResult, setShowValidationResult] = useState(false);
  const [skipValidation, setSkipValidation] = useState(false);

  // A result from one provider doesn't describe what another provider would
  // say — switching providers invalidates any result already on screen.
  useEffect(() => {
    invalidateValidationResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationProvider]);

  const handleValidateSelected = async () => {
    if (selectedEmails.size === 0) return;
    if (selectedEmails.size > MAX_VALIDATION_BATCH) {
      toast({
        title: "Too many selected",
        description: `Validate at most ${MAX_VALIDATION_BATCH} at a time — you have ${selectedEmails.size} selected.`,
        variant: "destructive",
      });
      return;
    }
    setValidating(true);
    setValidationResult(null);
    setShowValidationResult(false);
    try {
      const res = await axiosInstance.post(endpoints.flyer_validate, {
        emails: Array.from(selectedEmails), provider: validationProvider,
      });
      // The backend already wrote last_validated_at/last_validation_status to
      // Agent_DB/AdvertisementLead synchronously inside this call — nothing
      // further is needed to persist it, and it never depends on Launch.
      setValidationResult(res.data);
      // Detailed per-email list stays collapsed until "Show Result" is clicked —
      // only the Total/Valid/Invalid summary shows by default.
      setShowValidationResult(false);
      if (res.data.quota) setQuota(res.data.quota);
      // Reflect the fresh result onto each selected lead's tracked details so the
      // "must validate first" launch gate below sees the up-to-date status right away.
      setSelectedLeadDetails((prev) => {
        const next = { ...prev };
        for (const r of res.data.results || []) {
          if (next[r.email]) {
            // lead_status is the backend's own authoritative verdict — prefer
            // it over re-deriving from result/is_valid here, since that
            // duplication is exactly what let this page fall out of sync
            // with the backend's classification rule before (NeverBounce
            // "unknown" being reclassified to not_guaranteed, for one).
            // An error result has no lead_status (r.status !== "ok") and
            // must never overwrite whatever status the lead already had.
            const status = r.lead_status ?? (r.status === "ok" ? leadStatusForResult(r, validationProvider) : undefined);
            if (status) next[r.email] = { ...next[r.email], last_validation_status: status };
          }
        }
        return next;
      });
      // Force a real re-fetch of the picker's current page so its Validation
      // column reflects the just-written DB status right away (setting page/
      // search to their own current value is a no-op in React — it wouldn't
      // actually re-run the fetch).
      setLeadsRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast({
        title: "Validation failed",
        description: err?.response?.data?.error || "Could not validate the selected emails.",
        variant: "destructive",
      });
    } finally {
      setValidating(false);
    }
  };

  /* ── Upload CSV and Send Flyer ── */
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvSummary, setCsvSummary] = useState<any>(null);
  const csvInputRef = useRef<HTMLInputElement | null>(null);

  const [manualEmails, setManualEmails] = useState("");
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualSummary, setManualSummary] = useState<any>(null);

  const mergeContactSelection = (contacts: Array<{ email: string; name: string; last_validation_status: string | null }>) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      contacts.forEach((c) => next.add(c.email));
      return next;
    });
    setSelectedLeadDetails((prev) => {
      const next = { ...prev };
      contacts.forEach((c) => {
        next[c.email] = {
          id: 0, email: c.email, name: c.name, company_name: "",
          mail_status: "active", last_flyer_sent_at: null, flyer_sent_count: 0,
          last_validated_at: null, last_validation_status: c.last_validation_status,
          validation_count: 0,
        };
      });
      return next;
    });
    invalidateValidationResult();
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setCsvSummary(null);
    try {
      const formData = new FormData();
      formData.append("csv_file", csvFile);
      const res = await axiosInstance.post(endpoints.flyer_csv_upload, formData);
      setCsvSummary(res.data);

      // Merge (not replace) into the shared selection — the CSV contacts join
      // whatever's already picked from Search & Select, since selection is
      // universal across both entry points.
      const contacts: Array<{ email: string; name: string; last_validation_status: string | null }> = res.data.contacts || [];
      mergeContactSelection(contacts);

      toast({
        title: "CSV processed",
        description: `${res.data.valid_rows} contact(s) ready — ${res.data.created_count} newly added as leads.`,
      });
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = "";
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err?.response?.data?.error || "Could not process the CSV.",
        variant: "destructive",
      });
    } finally {
      setCsvUploading(false);
    }
  };

  const handleManualEmailsSubmit = async () => {
    const trimmed = manualEmails.trim();
    if (!trimmed) return;

    setManualSubmitting(true);
    setManualSummary(null);
    try {
      const res = await axiosInstance.post(endpoints.flyer_manual_emails, { emails: trimmed });
      setManualSummary(res.data);

      const contacts: Array<{ email: string; name: string; last_validation_status: string | null }> = res.data.contacts || [];
      mergeContactSelection(contacts);

      toast({
        title: "Manual emails processed",
        description: `${res.data.valid_rows} contact(s) ready — ${res.data.created_count} newly added as leads.`,
      });
      setManualEmails("");
    } catch (err: any) {
      toast({
        title: "Manual email check failed",
        description: err?.response?.data?.error || "Could not process the entered emails.",
        variant: "destructive",
      });
    } finally {
      setManualSubmitting(false);
    }
  };

  /* ── Launch modal ── */
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchMode, setLaunchMode] = useState<LaunchMode>("selected");
  const [launchSubject, setLaunchSubject] = useState("");
  const [launchTemplate, setLaunchTemplate] = useState("");
  const [launchLimit, setLaunchLimit] = useState("");
  const [launching, setLaunching] = useState(false);

  const openLaunchModal = (mode: LaunchMode) => {
    setLaunchMode(mode);
    setLaunchSubject(meta?.default_subject || "");
    setLaunchTemplate(meta?.default_template || "");
    setLaunchLimit("");
    setLaunchOpen(true);
  };

  const maxSelected = meta?.max_selected_emails || 20000;

  /* ── Template preview ── */
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Strips anything clickable/actionable out of the rendered flyer so the
  // preview is look-but-don't-touch — links can't navigate and forms can't
  // submit — while leaving normal scrolling untouched (unlike pointer-events:
  // none on the iframe itself, which would also block scrolling the preview).
  const sanitizeForPreview = (html: string) => {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll("a").forEach((a) => {
        a.removeAttribute("href");
        a.removeAttribute("onclick");
        (a as HTMLElement).style.cursor = "default";
      });
      doc.querySelectorAll("form").forEach((f) => f.removeAttribute("action"));
      doc.querySelectorAll("button").forEach((b) => b.setAttribute("disabled", "true"));
      return doc.documentElement.outerHTML;
    } catch {
      return html;
    }
  };

  const handlePreview = async () => {
    if (!launchTemplate.trim()) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewHtml(null);
    setPreviewError(null);
    try {
      const res = await axiosInstance.get(endpoints.flyer_preview, {
        params: { template_path: launchTemplate.trim() },
      });
      if (res.data.error) setPreviewError(res.data.error);
      else setPreviewHtml(sanitizeForPreview(res.data.html));
    } catch (err: any) {
      setPreviewError(err?.response?.data?.error || "Could not load the preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const templateField = (
    <div>
      <label className="text-xs font-medium text-gray-500 mb-1.5 block">Template path</label>
      <div className="flex gap-2">
        <Input
          value={launchTemplate}
          onChange={(e) => setLaunchTemplate(e.target.value)}
          placeholder="campaign/templates/advertise/flyer.html"
        />
        <Button
          type="button" variant="outline"
          className="flex-shrink-0 gap-1.5 text-xs h-9"
          disabled={!launchTemplate.trim() || previewLoading}
          onClick={handlePreview}
        >
          {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          Preview
        </Button>
      </div>
    </div>
  );

  const handleLaunch = async () => {
    const hasPermissionForMode = launchMode === "selected"
      ? permissions.canSendSelected
      : permissions.isSuperUser || permissions.canSendBulkFlyer;
    if (!hasPermissionForMode) {
      toast({ title: "Not permitted", description: "You don't have permission to send this flyer.", variant: "destructive" });
      return;
    }
    if (!launchSubject.trim() || !launchTemplate.trim()) {
      toast({ title: "Missing fields", description: "Subject and template path are required.", variant: "destructive" });
      return;
    }
    const payload: any = { subject: launchSubject.trim(), template_path: launchTemplate.trim() };
    if (launchMode === "selected") {
      if (selectedEmails.size === 0) {
        toast({ title: "Nothing selected", description: "Select at least one recipient first.", variant: "destructive" });
        return;
      }
      if (selectedEmails.size > maxSelected) {
        toast({ title: "Too many selected", description: `Maximum ${maxSelected.toLocaleString()} recipients per launch.`, variant: "destructive" });
        return;
      }
      // Skip Validation defaults to off — unvalidated (or previously-invalid)
      // selections block the launch until either validated or explicitly skipped.
      if (!skipValidation && unvalidatedSelectedCount > 0) {
        toast({
          title: "Unvalidated emails selected",
          description: `${unvalidatedSelectedCount} of ${selectedEmails.size} selected email(s) haven't been confirmed deliverable. Validate them first, or turn on Skip Validation to send anyway.`,
          variant: "destructive",
        });
        return;
      }
      payload.email_source = "selected";
      payload.emails = Array.from(selectedEmails);
      if (launchValidationJobId !== null) payload.validation_job_id = launchValidationJobId;
    } else {
      payload.email_source = source;
      if (launchMode === "limit") {
        const limitNum = parseInt(launchLimit, 10);
        if (!limitNum || limitNum <= 0) {
          toast({ title: "Invalid limit", description: "Enter a positive number.", variant: "destructive" });
          return;
        }
        payload.limit = limitNum;
      }
    }

    setLaunching(true);
    try {
      const res = await axiosInstance.post(endpoints.flyer_send, payload);
      toast({ title: "Flyer queued", description: res.data.message || `Job #${res.data.job_id} queued.` });
      setLaunchOpen(false);
      clearSelection();
      fetchJobs();
      // The send is only queued here (last_mailed_at updates once it
      // actually goes out), so the banner would still show "pending" for a
      // beat if re-fetched now — dismiss it locally instead, since the
      // user just did exactly what it was asking. A stale/wrong dismiss
      // self-corrects on the next full reload either way.
      if (launchMode === "selected") setPendingSendDismissed(true);
    } catch (err: any) {
      toast({
        title: "Launch failed",
        description: err?.response?.data?.message || err?.response?.data?.error || "Could not launch the flyer send.",
        variant: "destructive",
      });
    } finally {
      setLaunching(false);
    }
  };

  /* ── Access gates ── */
  if (authStatus === "loading") return <FlyerControlSkeleton />;
  if (authStatus === "unavailable") {
    return <AdminAccessState pageTitle="Flyer Control" variant="unavailable" onRetry={retry} retrying={retrying} />;
  }
  if (authStatus === "denied") {
    return <AdminAccessState pageTitle="Flyer Control" variant="denied" />;
  }

  // Sendable = passes the launch gate — "valid" and "not_guaranteed"
  // (NeverBounce catchall) both count, only "invalid" is excluded.
  const validEmails: string[] = validationResult?.results
    ?.filter((r: any) => isSendableResult(r, validationProvider))
    .map((r: any) => r.email) || [];

  /* ===================== RENDER ===================== */
  return (
    <div className="flex h-screen overflow-hidden bg-[#f8f9fb]">
      <Sidebar registerMobileToggle={(fn) => { toggleMobileRef.current = fn; }} />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <PageHeader pageTitle="Flyer Control" onMenuClick={() => toggleMobileRef.current?.()} />

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-7 py-6 space-y-6 max-w-[1600px] mx-auto">

            {/* ── HEADER ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Flyer Control</h1>
                <p className="text-sm text-gray-500 mt-0.5">Search, validate, and send flyer emails at scale</p>
              </div>
              <Button
                variant="outline" size="sm"
                className="h-9 gap-2 text-xs font-medium border-gray-200 hover:bg-gray-50"
                onClick={() => { fetchMeta(); fetchJobs(); }}
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </Button>
            </div>

            {/* ── BULK VALIDATION STATUS (persists across dialog closes / page reloads) ── */}
            {bulkJob && !bulkJobDismissed && (
              <div className={`rounded-2xl border px-5 py-4 shadow-sm ${
                bulkJob.status === "complete" ? "bg-emerald-50 border-emerald-200"
                  : bulkJob.status === "failed" ? "bg-red-50 border-red-200"
                  : "bg-blue-50 border-blue-200"
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {bulkJob.status === "running" && <Loader2 className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" />}
                    {bulkJob.status === "complete" && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                    {bulkJob.status === "failed" && <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${
                        bulkJob.status === "complete" ? "text-emerald-900"
                          : bulkJob.status === "failed" ? "text-red-900" : "text-blue-900"
                      }`}>
                        {bulkJob.status === "running" && `Validating ${bulkJob.total.toLocaleString()} email(s) via NeverBounce — ${bulkJob.percent_complete}%`}
                        {bulkJob.status === "complete" && `Bulk validation complete — ${bulkJob.valid.toLocaleString()} valid, ${bulkJob.not_guaranteed.toLocaleString()} not guaranteed, ${bulkJob.invalid.toLocaleString()} invalid`}
                        {bulkJob.status === "failed" && "Bulk validation failed"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {bulkJob.status === "running" && "Running in the background — feel free to keep working, or close this tab and come back later."}
                        {bulkJob.status === "complete" && "The Search & Select picker below has been refreshed with the results."}
                        {bulkJob.status === "failed" && (bulkJob.failure_reason || "NeverBounce reported this job as failed.")}
                      </p>
                    </div>
                  </div>
                  {bulkJob.status !== "running" && (
                    <button
                      onClick={() => setBulkJobDismissed(true)}
                      className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1"
                      aria-label="Dismiss"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {bulkJob.status === "running" && (
                  <Progress value={bulkJob.percent_complete} className="h-1.5 mt-3" />
                )}
              </div>
            )}

            {/* ── UNSENT VALIDATED BATCH (survives a page reload — the job's
                 results live in the DB, this just finds them again) ── */}
            {pendingSendJob && !pendingSendDismissed && (
              <div className="rounded-2xl border px-5 py-4 shadow-sm bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-blue-900 truncate">
                        {pendingSendJob.pending_send_count.toLocaleString()} validated email(s) haven't been sent a flyer yet
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        From a validation job completed {pendingSendJob.completed_at ? new Date(pendingSendJob.completed_at).toLocaleString() : "earlier"}
                        {" "}({pendingSendJob.valid.toLocaleString()} valid, {pendingSendJob.not_guaranteed.toLocaleString()} not guaranteed).
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm" className="h-8 gap-1.5 text-xs"
                      disabled={loadingPendingSend}
                      onClick={handleSelectAndSendPending}
                    >
                      {loadingPendingSend ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Select &amp; Send
                    </Button>
                    <button
                      onClick={() => setPendingSendDismissed(true)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      aria-label="Dismiss"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── STATS ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Active Agents" value={meta?.active_agent_count ?? 0} loading={metaLoading}
                valueClass="text-gray-900" icon={<Users className="w-5 h-5 text-gray-500" />} />
              <StatCard label="Advertisement Leads" value={meta?.advertisement_lead_count ?? 0} loading={metaLoading}
                valueClass="text-gray-900" icon={<Mail className="w-5 h-5 text-gray-500" />} />
              <StatCard
                label={`Credits Used (${VALIDATION_PROVIDER_LABELS[quota?.provider ?? validationProvider]})`}
                value={quota ? `${quota.used.toLocaleString()} / ${quota.limit.toLocaleString()}` : "—"}
                loading={metaLoading || quotaLoading} valueClass="text-blue-600"
                icon={<ShieldCheck className="w-5 h-5 text-blue-500" />}
              />
              <StatCard
                label={`Credits Remaining (${VALIDATION_PROVIDER_LABELS[quota?.provider ?? validationProvider]})`}
                value={quota?.remaining?.toLocaleString() ?? "—"}
                loading={metaLoading || quotaLoading} valueClass="text-emerald-600"
                icon={<CheckCircle2 className="w-5 h-5 text-emerald-500" />}
              />
            </div>

            {/* ── ACTIVITY & HISTORY (jobs) ── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">Activity &amp; History</h2>
                <Button
                  variant="ghost" size="sm"
                  className="h-8 text-xs font-medium text-gray-500 hover:text-gray-900"
                  onClick={() => setShowJobHistory((v) => !v)}
                >
                  {showJobHistory ? "Hide History" : "Show History"}
                </Button>
              </div>

              {showJobHistory && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" style={{ minWidth: 980 }}>
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/80">
                        {["Subject", "Status", "Progress", "Validated", "Failed", "Pending", "Created By", "Started", ""].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {jobsLoading ? (
                        Array.from({ length: 4 }).map((_, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="px-4 py-3" colSpan={9}><Skeleton className="h-4 w-full" /></td>
                          </tr>
                        ))
                      ) : jobs.length === 0 ? (
                        <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-400">No flyer jobs yet.</td></tr>
                      ) : (
                        jobs.map((job) => job.type === "validation" ? (
                          <tr key={`v-${job.job_id}`} className="border-b border-gray-50 hover:bg-amber-50/30 transition-colors bg-amber-50/10">
                            <td className="px-4 py-3 text-sm text-gray-800 max-w-[280px] truncate">
                              <ShieldCheck className="w-3.5 h-3.5 text-amber-500 inline mr-1.5 -mt-0.5" />
                              #{job.job_id} — Email Validation ({job.total.toLocaleString()})
                            </td>
                            <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                            <td className="px-4 py-3 w-48">
                              {job.status === "running" ? (
                                <div className="flex items-center gap-2">
                                  <Progress value={job.percent_complete} className="h-2 w-24" />
                                  <span className="text-xs text-gray-500 whitespace-nowrap">{job.percent_complete}%</span>
                                </div>
                              ) : job.status === "failed" ? (
                                <span className="text-xs text-red-500 truncate" title={job.failure_reason || ""}>{job.failure_reason || "Failed"}</span>
                              ) : (
                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                  <span className="text-emerald-600">{job.valid} valid</span>
                                  {job.not_guaranteed > 0 && <span className="text-amber-600">, {job.not_guaranteed} not guaranteed</span>}
                                  <span className="text-red-500">, {job.invalid} invalid</span>
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400">—</td>
                            <td className="px-4 py-3 text-sm text-gray-400">—</td>
                            <td className="px-4 py-3 text-sm text-gray-400">—</td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{job.created_by || "—"}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(job.created_at)}</td>
                            <td className="px-4 py-3">
                              {job.status === "complete" && (job.valid + job.not_guaranteed) > 0 && (
                                <Button
                                  size="sm" className="h-7 text-xs gap-1"
                                  disabled={loadingPendingSend}
                                  onClick={() => handleSendFlyerForJob(job)}
                                >
                                  {loadingPendingSend ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                  Send Flyer
                                </Button>
                              )}
                            </td>
                          </tr>
                        ) : (
                          <tr key={`f-${job.job_id}`} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors">
                            <td className="px-4 py-3 text-sm text-gray-800 max-w-[280px] truncate" title={job.subject}>#{job.job_id} — {job.subject}</td>
                            <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                            <td className="px-4 py-3 w-40">
                              <div className="flex items-center gap-2">
                                <Progress value={job.progress_pct} className="h-2 w-24" />
                                <span className="text-xs text-gray-500 whitespace-nowrap">{job.sent}/{job.total}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${job.validated ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                                {job.validated ? "Yes" : "No"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-red-500">{job.failed}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{job.pending}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{job.created_by || "—"}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {job.started_at ? formatDateTime(job.started_at) : "—"}
                            </td>
                            <td className="px-4 py-3">
                              {(job.status === "pending" || job.status === "running") && (
                                <Button
                                  variant="outline" size="sm"
                                  className="h-7 text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => setCancelTarget(job)}
                                  disabled={cancelling}
                                >
                                  <Ban className="w-3.5 h-3.5" /> Cancel
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── SEARCH & SELECT ── */}
            {permissions.canSendSelected && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Search &amp; Select Recipients</h2>

                <div className="flex flex-wrap gap-2 mb-3">
                  {(["agents", "advertisement_leads"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => { setSource(s); setLeadsPage(1); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        source === s ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {s === "agents" ? "Agents" : "Advertisement Leads"}
                    </button>
                  ))}
                </div>

                <div className="relative w-full mb-3">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, or company…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-10 h-9 text-sm border-gray-200 bg-gray-50 rounded-xl focus:bg-white w-full"
                  />
                </div>

                {/* Primary filters — the three most-used statuses, always visible */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Select value={flyerStatus} onValueChange={(v) => { setFlyerStatus(v); if (v !== "sent") { setDateFrom(""); setDateTo(""); } setLeadsPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl"><SelectValue placeholder="Flyer status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All flyer status</SelectItem>
                      <SelectItem value="sent">Previously sent</SelectItem>
                      <SelectItem value="not_sent">Never sent</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={validationStatus} onValueChange={(v) => { setValidationStatus(v); if (v === "all") { setValidatedDateFrom(""); setValidatedDateTo(""); } setLeadsPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl"><SelectValue placeholder="Validation status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All validation status</SelectItem>
                      <SelectItem value="valid">Valid</SelectItem>
                      <SelectItem value="not_guaranteed">Not Guaranteed</SelectItem>
                      <SelectItem value="invalid">Invalid</SelectItem>
                      <SelectItem value="not_validated">Not validated</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={engagementStatus} onValueChange={(v) => { setEngagementStatus(v); setLeadsPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl"><SelectValue placeholder="Engagement" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All engagement</SelectItem>
                      <SelectItem value="opened">Opened</SelectItem>
                      <SelectItem value="clicked">Clicked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Secondary filters — tucked away by default, only shown on demand */}
                <button
                  onClick={() => setShowMoreFilters((v) => !v)}
                  className="mt-2 text-xs font-medium text-gray-400 hover:text-gray-700"
                >
                  {showMoreFilters ? "Hide more filters" : "More filters"}
                </button>

                {showMoreFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
                    {source === "agents" && (
                      <Select value={importSource || "__all__"} onValueChange={(v) => { setImportSource(v === "__all__" ? "" : v); setLeadsPage(1); }}>
                        <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl"><SelectValue placeholder="Import source" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All import sources</SelectItem>
                          {(meta?.import_sources || []).map((src) => (
                            <SelectItem key={src} value={src}>{src}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    <Input
                      placeholder="Exclude domains (gmail.com,yahoo.com)"
                      value={excludeDomains}
                      onChange={(e) => { setExcludeDomains(e.target.value); setLeadsPage(1); }}
                      className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl"
                    />

                    {flyerStatus === "sent" && (
                      <>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400 whitespace-nowrap">Sent from</label>
                          <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setLeadsPage(1); }} className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl" />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400 whitespace-nowrap">Sent to</label>
                          <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setLeadsPage(1); }} className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl" />
                        </div>
                      </>
                    )}
                    {validationStatus !== "all" && (
                      <>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400 whitespace-nowrap">Validated from</label>
                          <Input type="date" value={validatedDateFrom} onChange={(e) => { setValidatedDateFrom(e.target.value); setLeadsPage(1); }} className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl" />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400 whitespace-nowrap">Validated to</label>
                          <Input type="date" value={validatedDateTo} onChange={(e) => { setValidatedDateTo(e.target.value); setLeadsPage(1); }} className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl" />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Active filters — what's actually narrowing the list right now,
                    each removable on its own without opening any dropdown. */}
                {activeFilterChips.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-3">
                    {activeFilterChips.map((chip) => (
                      <button
                        key={chip.key}
                        onClick={() => { chip.clear(); setLeadsPage(1); }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-full transition-colors"
                      >
                        {chip.label}
                        <XCircle className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-3">
                  {activeFilterChips.length > 0 ? (
                    <button onClick={resetFilters} className="text-xs font-medium text-gray-400 hover:text-gray-700">Clear all filters</button>
                  ) : <span />}
                  <p className="text-xs text-gray-400">{leadsCount.toLocaleString()} matching {source === "agents" ? "agents" : "leads"}</p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" style={{ minWidth: 900 }}>
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/80">
                      <th className="px-4 py-3 w-10">
                        <Checkbox checked={pageAllSelected} onCheckedChange={togglePageAll} />
                      </th>
                      {["Email", "Name / Company", "Status", "Flyer Sent", "Engagement", "Validation"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={leadsFiltering ? "opacity-40 transition-opacity" : "transition-opacity"}>
                    {leadsLoading ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="px-4 py-3" colSpan={7}><Skeleton className="h-4 w-full" /></td>
                        </tr>
                      ))
                    ) : leads.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-gray-400">No results match the current filters.</td></tr>
                    ) : (
                      leads.map((lead) => (
                        <tr key={lead.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors">
                          <td className="px-4 py-3">
                            <Checkbox checked={selectedEmails.has(lead.email)} onCheckedChange={() => toggleEmail(lead)} />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{lead.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                            {lead.name || "—"}{lead.company_name ? ` · ${lead.company_name}` : ""}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{lead.mail_status}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openHistory(lead.email, "flyer")}
                              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-600"
                            >
                              <History className="w-3.5 h-3.5" />
                              {lead.flyer_sent_count > 0 ? `Sent ${lead.flyer_sent_count}×` : "Never sent"}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <span
                                title={lead.last_opened_at ? `Opened ${formatDateTime(lead.last_opened_at)}` : "Not opened"}
                                className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${lead.last_opened_at ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-400"}`}
                              >
                                Opened
                              </span>
                              <span
                                title={lead.last_clicked_at ? `Clicked ${formatDateTime(lead.last_clicked_at)}` : "Not clicked"}
                                className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${lead.last_clicked_at ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-400"}`}
                              >
                                Clicked
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openHistory(lead.email, "validation")}
                              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full hover:opacity-80 ${
                                lead.last_validation_status ? VALIDATION_STYLES[lead.last_validation_status] || "bg-gray-100 text-gray-600" : "bg-gray-100 text-gray-400"
                              }`}
                            >
                              {lead.last_validation_status
                                ? `${VALIDATION_STATUS_LABELS[lead.last_validation_status] || lead.last_validation_status} (${lead.validation_count}×)`
                                : "Not validated"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <p className="text-xs text-gray-400 font-medium">
                    Page {leadsPage} of {leadsNumPages} &middot; {leadsCount.toLocaleString()} total
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400 font-medium">Rows per page</span>
                    <Select
                      value={String(leadsPageSize)}
                      onValueChange={(v) => { setLeadsPageSize(Number(v)); setLeadsPage(1); }}
                    >
                      <SelectTrigger className="h-8 w-[90px] text-xs border-gray-200 bg-gray-50 rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEADS_PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setLeadsPage((p) => Math.max(1, p - 1))}
                    disabled={leadsLoading || leadsPage <= 1}
                    className="h-8 w-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {getPaginationItems(leadsPage, leadsNumPages).map((item, idx) =>
                    item === "..." ? (
                      <span key={`e-${idx}`} className="w-8 text-center text-xs text-gray-400">…</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setLeadsPage(item as number)}
                        disabled={leadsLoading}
                        className={`h-8 min-w-8 px-2 rounded-lg text-xs font-semibold ${
                          item === leadsPage ? "bg-blue-600 text-white shadow-sm shadow-blue-200" : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setLeadsPage((p) => Math.min(leadsNumPages, p + 1))}
                    disabled={leadsLoading || leadsPage >= leadsNumPages}
                    className="h-8 w-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Selection bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">{selectedEmails.size.toLocaleString()} selected</span>
                  {selectedEmails.size > 0 && (
                    <button onClick={clearSelection} className="text-xs text-gray-400 hover:text-gray-700">
                      Clear selection
                    </button>
                  )}
                </div>
                <Button
                  size="sm" className="h-9 gap-2 text-xs bg-blue-600 hover:bg-blue-700"
                  disabled={selectedEmails.size === 0}
                  onClick={() => openLaunchModal("selected")}
                >
                  <Send className="w-3.5 h-3.5" /> Proceed
                </Button>
              </div>
            </div>
            )}

            {/* ── Upload CSV and Send Flyer ── */}
            {(permissions.isSuperUser || permissions.canUploadCsvFlyer) && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Upload CSV and Send Flyer</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Upload a CSV of email/name pairs. Each address is checked against Agents and Advertisement
                      Leads — anything not found is added as a new Advertisement Lead automatically.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                      className="max-w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                    />
                    <Button
                      size="sm" className="h-9 text-xs gap-2 bg-gray-900 hover:bg-gray-800"
                      disabled={!csvFile || csvUploading}
                      onClick={handleCsvUpload}
                    >
                      {csvUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      Upload &amp; Check
                    </Button>
                  </div>
                </div>

                {csvSummary && (
                  <div className="mt-4 rounded-xl border border-gray-200 p-4">
                    <div className="flex flex-wrap gap-4 text-sm mb-2">
                      <span className="text-gray-500">Total rows: <strong className="text-gray-900">{csvSummary.total_rows}</strong></span>
                      <span className="text-gray-500">Already agents: <strong className="text-gray-900">{csvSummary.existing_agent_count}</strong></span>
                      <span className="text-gray-500">Already leads: <strong className="text-gray-900">{csvSummary.existing_lead_count}</strong></span>
                      <span className="text-emerald-600">Newly added: <strong>{csvSummary.created_count}</strong></span>
                      {csvSummary.invalid_rows > 0 && <span className="text-red-500">Invalid: <strong>{csvSummary.invalid_rows}</strong></span>}
                      {csvSummary.duplicate_rows > 0 && <span className="text-amber-600">Duplicates: <strong>{csvSummary.duplicate_rows}</strong></span>}
                    </div>
                    {csvSummary.error_rows?.length > 0 && (
                      <details className="text-xs text-gray-500 mb-3">
                        <summary className="cursor-pointer text-gray-600 font-medium">{csvSummary.error_rows.length} row(s) skipped — details</summary>
                        <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                          {csvSummary.error_rows.map((e: any, i: number) => (
                            <li key={i}>Row {e.row_number}: {e.email || "(blank)"} — {e.reason}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                    <Button
                      size="sm" className="h-9 text-xs gap-2 bg-blue-600 hover:bg-blue-700"
                      disabled={selectedEmails.size === 0 || !permissions.canSendSelected}
                      onClick={() => openLaunchModal("selected")}
                    >
                      <Send className="w-3.5 h-3.5" /> Proceed with {selectedEmails.size.toLocaleString()} Selected
                    </Button>
                  </div>
                )}

                {(permissions.isSuperUser || permissions.canSendFlyer) && (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Enter emails manually</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Paste comma-separated or newline-separated emails. Matching records are reused, and missing emails are added as new Advertisement Leads before validation and sending.
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 space-y-3">
                      <textarea
                        value={manualEmails}
                        onChange={(e) => setManualEmails(e.target.value)}
                        rows={4}
                        placeholder="name@example.com, another@example.com\nthird@example.com"
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="h-9 text-xs gap-2 bg-gray-900 hover:bg-gray-800"
                          disabled={!manualEmails.trim() || manualSubmitting}
                          onClick={handleManualEmailsSubmit}
                        >
                          {manualSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                          Add &amp; Check
                        </Button>
                      </div>
                    </div>

                    {manualSummary && (
                      <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                        <div className="flex flex-wrap gap-4 text-sm mb-2">
                          <span className="text-gray-500">Total rows: <strong className="text-gray-900">{manualSummary.total_rows}</strong></span>
                          <span className="text-gray-500">Already agents: <strong className="text-gray-900">{manualSummary.existing_agent_count}</strong></span>
                          <span className="text-gray-500">Already leads: <strong className="text-gray-900">{manualSummary.existing_lead_count}</strong></span>
                          <span className="text-emerald-600">Newly added: <strong>{manualSummary.created_count}</strong></span>
                          {manualSummary.invalid_rows > 0 && <span className="text-red-500">Invalid: <strong>{manualSummary.invalid_rows}</strong></span>}
                          {manualSummary.duplicate_rows > 0 && <span className="text-amber-600">Duplicates: <strong>{manualSummary.duplicate_rows}</strong></span>}
                        </div>
                        {manualSummary.error_rows?.length > 0 && (
                          <details className="text-xs text-gray-500 mb-3">
                            <summary className="cursor-pointer text-gray-600 font-medium">{manualSummary.error_rows.length} row(s) skipped — details</summary>
                            <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                              {manualSummary.error_rows.map((e: any, i: number) => (
                                <li key={i}>{e.email || "(blank)"} — {e.reason}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Bulk send (blanket source) ── */}
            {(permissions.isSuperUser || permissions.canSendBulkFlyer) && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Bulk Send</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Send to every active agent / lead (excludes invalid-validated addresses automatically), or cap with a custom limit.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => openLaunchModal("limit")}>Custom Limit</Button>
                  <Button size="sm" className="h-9 text-xs bg-gray-900 hover:bg-gray-800" onClick={() => openLaunchModal("full")}>Full Run</Button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Cancel confirm ── */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) setCancelTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-red-500" /> Cancel Flyer Job</AlertDialogTitle>
            <AlertDialogDescription>
              Cancel job <strong className="text-gray-900">#{cancelTarget?.job_id}</strong>? Pending recipients will be stopped before reaching Mailgun; emails already in the active batch cannot be recalled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelTarget(null)}>Keep Running</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelConfirm} className="bg-red-600 hover:bg-red-700">Yes, Cancel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── History modal ── */}
      <Dialog open={!!historyTarget} onOpenChange={(open) => { if (!open) setHistoryTarget(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{historyTarget?.type === "flyer" ? "Flyer Send History" : "Validation History"}</DialogTitle>
            <DialogDescription className="break-all">{historyTarget?.email}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 max-h-80 overflow-y-auto pr-1">
            {historyLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : historyData?.error ? (
              <p className="text-sm text-red-500 py-4">{historyData.error}</p>
            ) : historyData?.history?.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">No history yet.</p>
            ) : historyTarget?.type === "flyer" ? (
              <ul className="divide-y divide-gray-100">
                {historyData?.history?.map((h: any, i: number) => (
                  <li key={i} className="py-3 text-sm">
                    <p className="text-gray-900 font-medium truncate">{h.subject}</p>
                    <p className="text-xs text-gray-400 mt-1">{h.sent_at ? formatDateTime(h.sent_at) : "—"}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="divide-y divide-gray-100">
                {historyData?.history?.map((h: any, i: number) => {
                  const notGuaranteed = h.provider === "neverbounce" && NEVERBOUNCE_NOT_GUARANTEED_RESULTS.has(h.result);
                  const sendable = h.is_valid || notGuaranteed;
                  return (
                    <li key={i} className="py-3 text-sm flex items-center justify-between gap-3">
                      <div>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                          notGuaranteed ? "bg-amber-50 text-amber-700" : sendable ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                        }`}>
                          {sendable ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                          {h.result} · {h.risk || "unknown"} risk
                        </span>
                        <p className="text-xs text-gray-400 mt-1">
                          {VALIDATION_PROVIDER_LABELS[h.provider as ValidationProvider] || h.provider} &middot;{" "}
                          {h.validated_at ? formatDateTime(h.validated_at) : "—"}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Proceed / Launch modal — universal for Search & Select and CSV upload ── */}
      <Dialog open={launchOpen} onOpenChange={setLaunchOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-500" /> {launchMode === "selected" ? "Proceed to Send" : "Launch Flyer"}
            </DialogTitle>
            <DialogDescription>
              {launchMode === "selected" && `Sending to ${selectedEmails.size.toLocaleString()} selected recipient(s).`}
              {launchMode === "full" && `Sending to every active ${source === "agents" ? "agent" : "advertisement lead"} (invalid-validated addresses excluded automatically).`}
              {launchMode === "limit" && `Sending to a capped number of ${source === "agents" ? "agents" : "advertisement leads"} (invalid-validated addresses excluded automatically).`}
            </DialogDescription>
          </DialogHeader>

          {launchMode === "selected" ? (
            <div className="mt-5 rounded-2xl border border-gray-200 overflow-hidden divide-y divide-gray-200">
              {/* Step 1 — Validate */}
              <div className={skipValidation ? "p-5 bg-gray-50" : "p-5 bg-blue-50/50"}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white flex-shrink-0 ${skipValidation ? "bg-gray-300" : "bg-blue-600"}`}>1</span>
                    <span className={`text-sm font-semibold ${skipValidation ? "text-gray-400" : "text-gray-900"}`}>Validate recipients</span>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 flex-shrink-0">
                    Skip
                    <Switch checked={skipValidation} onCheckedChange={setSkipValidation} />
                  </label>
                </div>

                {!skipValidation ? (
                  <div className="mt-4 pl-8 space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">Validate via</span>
                      <Select
                        value={validationProvider}
                        onValueChange={(v) => setValidationProvider(v as ValidationProvider)}
                        disabled={validating}
                      >
                        <SelectTrigger className="h-8 w-[150px] text-xs bg-white border-gray-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="neverbounce">NeverBounce</SelectItem>
                          <SelectItem value="mailgun">Mailgun</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedEmails.size > MAX_VALIDATION_BATCH && validationProvider === "neverbounce" ? (
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          className="gap-2 bg-white border-gray-300 shadow-sm hover:bg-gray-50"
                          disabled={startingBulkJob || bulkJob?.status === "running"}
                          onClick={handleStartBulkValidation}
                        >
                          {startingBulkJob ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
                          ) : bulkJob?.status === "running" ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Validating in background ({bulkJob.percent_complete}%)…</>
                          ) : (
                            <><ShieldCheck className="w-4 h-4 text-blue-600" /> Start Background Validation ({selectedEmails.size.toLocaleString()} Emails)</>
                          )}
                        </Button>
                        <p className="text-xs text-gray-400">
                          {selectedEmails.size.toLocaleString()} emails is too many to check instantly — this runs as a
                          NeverBounce bulk job. Feel free to close this dialog; progress shows at the top of the page,
                          and results land here once it's done.
                        </p>
                      </div>
                    ) : !validationResult ? (
                      <Button
                        variant="outline"
                        className="gap-2 bg-white border-gray-300 shadow-sm hover:bg-gray-50"
                        disabled={selectedEmails.size === 0 || validating}
                        onClick={handleValidateSelected}
                      >
                        {validating ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Validating…</>
                        ) : (
                          <><ShieldCheck className="w-4 h-4 text-blue-600" /> Validate {selectedEmails.size.toLocaleString()} Email(s)</>
                        )}
                      </Button>
                    ) : (
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex flex-wrap gap-3 text-xs">
                          <span className="text-gray-500">Total: <strong className="text-gray-900">{validationResult.total}</strong></span>
                          <span className="text-emerald-600">
                            Valid: <strong>{validationResult.valid}</strong>
                            {validationResult.not_guaranteed > 0 && (
                              <span className="text-amber-700 font-normal"> ({validationResult.not_guaranteed} not guaranteed)</span>
                            )}
                          </span>
                          <span className="text-red-500">Invalid: <strong>{validationResult.invalid}</strong></span>
                          {validationResult.errors > 0 && <span className="text-amber-600">Errors: <strong>{validationResult.errors}</strong></span>}
                        </div>
                        <Button
                          variant="outline" size="sm" className="h-7 gap-1.5 text-xs bg-white border-gray-300"
                          onClick={() => setShowValidationResult((v) => !v)}
                        >
                          {showValidationResult ? "Hide Result" : "Show Result"}
                        </Button>
                      </div>
                    )}

                    {unvalidatedSelectedCount > 0 && (
                      <p className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        {unvalidatedSelectedCount} of {selectedEmails.size} not yet confirmed deliverable
                      </p>
                    )}

                    {validationResult && showValidationResult && (
                      <div className="rounded-lg bg-white border border-gray-200 p-4">
                        <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100 pr-1">
                          {validationResult.results.map((r: any) => {
                            const isError = r.status === "error";
                            const leadStatus = isError ? null : (r.lead_status ?? leadStatusForResult(r, validationProvider));
                            return (
                              <li key={r.email} className="flex items-center justify-between gap-3 py-2 text-xs">
                                <span className="text-gray-700 truncate">
                                  {r.email}
                                  {r.skipped_recent && (
                                    <span className="ml-1.5 text-gray-400" title={`Already validated within the last ${RECENT_VALIDATION_FRESHNESS_DAYS} days — not re-checked`}>
                                      (cached)
                                    </span>
                                  )}
                                </span>
                                <span className={`flex-shrink-0 font-semibold px-2 py-0.5 rounded-full ${isError ? "text-gray-600 bg-gray-100" : VALIDATION_STYLES[leadStatus as string]}`}>
                                  {isError ? "Error" : VALIDATION_STATUS_LABELS[leadStatus as string]}
                                </span>
                              </li>
                            );
                          })}
                        </ul>

                        {validEmails.length > 0 && validEmails.length < selectedEmails.size && (
                          <button
                            onClick={() => setSelectedEmails(new Set(validEmails))}
                            className="mt-3 text-xs font-medium text-blue-600 hover:underline"
                          >
                            Keep only the {validEmails.length} sendable email(s)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-2 pl-8 text-xs text-gray-400">Skipped — sending without validation.</p>
                )}
              </div>

              {/* Step 2 — Compose & send */}
              <div className="p-5 bg-white">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white bg-blue-600 flex-shrink-0">
                    {skipValidation ? "1" : "2"}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">Compose &amp; send</span>
                </div>
                <div className="pl-8 space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1.5 block">Subject</label>
                    <Input value={launchSubject} onChange={(e) => setLaunchSubject(e.target.value)} placeholder="Subject line" />
                  </div>
                  {templateField}
                  {selectedEmails.size > maxSelected && (
                    <p className="text-xs text-red-500">Selection exceeds the {maxSelected.toLocaleString()} recipient cap per launch.</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">Subject</label>
                <Input value={launchSubject} onChange={(e) => setLaunchSubject(e.target.value)} placeholder="Subject line" />
              </div>
              {templateField}
              {launchMode === "limit" && (
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Limit</label>
                  <Input type="number" min={1} value={launchLimit} onChange={(e) => setLaunchLimit(e.target.value)} placeholder="e.g. 500" />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setLaunchOpen(false)} disabled={launching}>Cancel</Button>
            <Button
              onClick={handleLaunch}
              disabled={
                launching ||
                (launchMode === "selected"
                  ? !permissions.canSendSelected
                  : !(permissions.isSuperUser || permissions.canSendBulkFlyer))
              }
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              {launching ? "Launching…" : "Launch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Template preview ── */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-blue-500" /> Template Preview</DialogTitle>
            <DialogDescription className="break-all">{launchTemplate || "—"}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden bg-white" style={{ height: "78vh", minHeight: 480, maxHeight: 900 }}>
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : previewError ? (
              <div className="p-4 text-sm text-red-500">{previewError}</div>
            ) : previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                title="Flyer preview"
                className="w-full h-full"
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="p-4 text-sm text-gray-400">Enter a template path and click Preview.</div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const FlyerControlSkeleton: React.FC = () => (
  <div className="flex h-screen overflow-hidden bg-[#f8f9fb]">
    <Sidebar />
    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
      <PageHeader pageTitle="Flyer Control" />
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-7 py-6 space-y-6 max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between">
            <div><Skeleton className="h-8 w-48" /><Skeleton className="h-4 w-64 mt-1.5" /></div>
            <Skeleton className="h-9 w-20" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-40" />
          <Skeleton className="h-96" />
        </div>
      </div>
    </div>
  </div>
);

export default FlyerControl;
